import { modalAction } from '@/utils/modal';
import cn from 'classnames';
import Spinner from '@/components/UI/Spinner';
import ReasonForChange from '@/components/Shared/Latest/ReasonForChange';
import { featuresSelector } from '@/support/Selectors';
import { createSelector } from '@reduxjs/toolkit';
import { CSSProperties, FC, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form@latest';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../ConfirmDelete/TimedDeleteConfirmation.module.scss';

const reasonForChangeFeatureSelector = createSelector([featuresSelector], (features) => ({
  reasonForChangeEnabled: features?.reason_for_change ?? false,
}));

interface ConfirmSamplePlanUpdateFormData {
  reason_for_change?: string;
}

interface ConfirmSamplePlanUpdateProps {
  affectedSampleCount: number;
  onClick: (reasonForChange?: string) => Promise<void>;
  onCancel?: () => void;
}

/**
 * Confirmation modal for sample plan updates that combines a timed
 * confirmation button with an optional Reason for Change field.
 * The reason field visibility is controlled by the reason_for_change feature flag.
 */
const ConfirmSamplePlanUpdate: FC<ConfirmSamplePlanUpdateProps> = ({ affectedSampleCount, onClick, onCancel }) => {
  const [timerElapsed, setTimerElapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { closeModal } = modalAction(useDispatch());
  const { reasonForChangeEnabled } = useSelector(reasonForChangeFeatureSelector);
  const formMethods = useForm<ConfirmSamplePlanUpdateFormData>();
  const { handleSubmit, watch } = formMethods;
  const reasonValue = watch('reason_for_change');
  const hasValidReason = !reasonForChangeEnabled || (reasonValue ?? '').trim().length >= 3;
  const buttonEnabled = timerElapsed && hasValidReason;

  const postfix = affectedSampleCount > 1 ? 's' : '';

  const onSubmit = async (form: ConfirmSamplePlanUpdateFormData): Promise<void> => {
    setIsLoading(true);
    try {
      await onClick(form.reason_for_change);
    } finally {
      setIsLoading(false);
    }
    closeModal();
  };

  return (
    <div className="center mv3 bg-white br2 shadow-4" style={{ maxWidth: '19rem' }}>
      <div className="pa3">
        <div className="pb3 tc">
          <h3 className="normal f4 pb2">{`Confirm sample update${postfix}`}</h3>
          <div className="f6">
            <p>
              This will update {affectedSampleCount} existing planned sample{postfix}.
            </p>
            <p>
              Saving will apply {affectedSampleCount > 1 ? 'these changes' : 'this change'} <strong>only</strong> to the
              metadata and details of those sample{postfix}.
            </p>
            <p>No samples will be created, deleted, or cancelled.</p>
          </div>
        </div>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)} data-testid="confirm-sample-plan-update-form">
            <ReasonForChange />
            <div className="pt2">
              {isLoading ? (
                <div className="flex flex-column items-center pv3" data-testid="loading-spinner">
                  <Spinner size="small" color="blue" />
                </div>
              ) : (
                <>
                  <button
                    type="submit"
                    className={cn(styles.deleteConfirmButton, styles.darkBlue)}
                    style={{ '--confirmation-animation-duration': '3s' } as CSSProperties}
                    onAnimationEnd={() => setTimerElapsed(true)}
                    disabled={!buttonEnabled}
                    data-testid="confirm-update-button"
                  >
                    <div className={styles.content}>{`Update ${affectedSampleCount} sample${postfix}`}</div>
                    <div className={styles.progress}></div>
                  </button>
                  <button
                    type="button"
                    className="button plain db w-100 mt2"
                    onClick={() => {
                      closeModal();
                      onCancel?.();
                    }}
                    data-testid="cancel-button"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default ConfirmSamplePlanUpdate;
